import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Login from './components/Login';
import { TerminalSidebar, TerminalHeader, MobileBottomNav } from './components/Navigation';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home';
import Alerts from './pages/Alerts';
import Calculator from './pages/Calculator';
import Position from './pages/Position';
import PNL from './pages/PNL';
import MasterScripts from './pages/MasterScripts';
import OrderBook from './pages/OrderBook';
import OptionChain from './pages/OptionChain';
import './index.css';
import { playSound } from './utils/sounds';


const alertQuoteLtp = (q) => {
  const value = q?.ltp ?? q?.last_price ?? q?.lastPrice ?? q?.lp ?? q?.LTP;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};
const alertQuoteToken = (q) => String(q?.exchange_token ?? q?.exchangeToken ?? q?.token ?? q?.symbol ?? '').trim();

const PriceAlertMonitor = ({ enabled }) => {
  const activeAlertsRef = useRef([]);
  const permissionCheckedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;
    const API = process.env.REACT_APP_API_URL || '';
    const accountKey = localStorage.getItem('kotak_ucc') || localStorage.getItem('kotak_mobile') || 'default';
    let cancelled = false;
    let alertRefreshTimer;
    let quoteTimer;

    const loadActiveAlerts = async () => {
      try {
        const response = await axios.get(`${API}/api/alerts`, { params: { accountKey } });
        if (!cancelled) activeAlertsRef.current = (response.data?.alerts || []).filter(a => a.status === 'ACTIVE');
      } catch (error) {}
    };

    const checkPrices = async () => {
      const alerts = activeAlertsRef.current;
      if (cancelled || !alerts.length) return;
      const baseUrl = localStorage.getItem('kotak_baseurl');
      const accessToken = localStorage.getItem('kotak_access_token');
      if (!baseUrl || !accessToken) return;

      const uniqueSymbols = [...new Map(alerts.map(a => [`${a.exchangeSegment}|${a.instrumentToken}`, a])).values()];
      for (let start = 0; start < uniqueSymbols.length; start += 50) {
        const batch = uniqueSymbols.slice(start, start + 50);
        try {
          const symbols = batch.map(a => `${a.exchangeSegment}|${a.instrumentToken}`).join(',');
          const response = await axios.get(`${API}/api/kotak-quotes`, {
            params: { symbols, baseUrl },
            headers: { Authorization: accessToken }
          });
          const raw = Array.isArray(response.data) ? response.data : (response.data?.data || response.data?.data?.data || []);
          const quoteMap = {};
          raw.forEach(q => { const token = alertQuoteToken(q); if (token) quoteMap[token] = q; });
          const quotes = batch.map(a => ({
            exchangeSegment: a.exchangeSegment,
            instrumentToken: a.instrumentToken,
            symbol: a.symbol,
            price: alertQuoteLtp(quoteMap[String(a.instrumentToken)])
          })).filter(q => Number.isFinite(Number(q.price)));
          if (!quotes.length) continue;
          const evaluated = await axios.post(`${API}/api/alerts/evaluate`, { accountKey, quotes });
          const triggered = evaluated.data?.triggered || [];
          if (triggered.length) {
            playSound('priceAlert');
            activeAlertsRef.current = activeAlertsRef.current.filter(a => !triggered.some(n => Number(n.alertId) === Number(a.id)));
            window.dispatchEvent(new Event('mk-server-notifications-changed'));
            triggered.forEach(note => {
              if ('Notification' in window && Notification.permission === 'granted') {
                try { new Notification(note.title || 'Price Alert', { body: note.message }); } catch (e) {}
              }
            });
          }
        } catch (error) {
          // The normal trading screen remains usable if an alert quote check fails.
        }
      }
    };



    loadActiveAlerts();
    alertRefreshTimer = window.setInterval(loadActiveAlerts, 5000);
    quoteTimer = window.setInterval(checkPrices, 1000);
    window.addEventListener('mk-server-notifications-changed', loadActiveAlerts);
    return () => {
      cancelled = true;
      window.clearInterval(alertRefreshTimer);
      window.clearInterval(quoteTimer);
      window.removeEventListener('mk-server-notifications-changed', loadActiveAlerts);
    };
  }, [enabled]);

  return null;
};

const AppRoutes = () => <Routes>
  <Route path="/login" element={<Login />} />
  <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
  <Route path="/alerts" element={<ProtectedRoute><Alerts /></ProtectedRoute>} />
  <Route path="/orders" element={<Navigate to="/alerts" replace />} />
  <Route path="/calculator" element={<ProtectedRoute><Calculator /></ProtectedRoute>} />
  <Route path="/order-book" element={<ProtectedRoute><OrderBook /></ProtectedRoute>} />
  <Route path="/position" element={<ProtectedRoute><Position /></ProtectedRoute>} />
  <Route path="/pnl" element={<ProtectedRoute><PNL /></ProtectedRoute>} />
  <Route path="/watchlist" element={<ProtectedRoute><MasterScripts /></ProtectedRoute>} />
  <Route path="/option-chain" element={<ProtectedRoute><OptionChain /></ProtectedRoute>} />
  <Route path="/master-scripts" element={<Navigate to="/watchlist" replace />} />
</Routes>;

const AppLayout = () => {
  const location = useLocation();
  const isLoggedIn = localStorage.getItem('kotak_trading_token');
  const hideTerminal = location.pathname === '/login' || !isLoggedIn;
  const [expanded, setExpanded] = useState(false); // compact sidebar by default
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const sync = () => { if (window.innerWidth >= 768) setMobileOpen(false); };
    sync(); window.addEventListener('resize', sync); return () => window.removeEventListener('resize', sync);
  }, []);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (hideTerminal) return undefined;
    let cancelled = false;
    const clearClientSession = () => {
      ['kotak_trading_token','kotak_sid1','kotak_sid2','kotak_auth','kotak_baseurl','kotak_server_id'].forEach((key) => localStorage.removeItem(key));
      if (!cancelled) window.location.replace('/login');
    };
    const checkServerSession = async () => {
      try {
        const API = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${API}/api/session/status`, { credentials: 'include', cache: 'no-store' });
        if (!response.ok) { clearClientSession(); return; }
        const data = await response.json();
        if (!data.active || (data.expiresAt && Date.now() >= Number(data.expiresAt))) clearClientSession();
      } catch (error) {
        // Keep the trading screen usable during a temporary network interruption.
      }
    };
    checkServerSession();
    const timer = window.setInterval(checkServerSession, 30000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [hideTerminal]);

  if (hideTerminal) return <AppRoutes />;
  const sidebarClass = expanded ? 'col-md-2' : 'col-md-1';
  const mainClass = expanded ? 'col-md-10' : 'col-md-11';
  const toggle = () => {
    if (window.innerWidth < 768) setMobileOpen(v => !v);
    else setExpanded(v => !v);
  };

  return (
    <>
      <PriceAlertMonitor enabled={!hideTerminal} />
      <div className={`container-fluid p-0 mk-app-shell ${expanded ? 'mk-sidebar-expanded' : 'mk-sidebar-collapsed'} ${mobileOpen ? 'mk-mobile-sidebar-open' : ''}`}>
      <div className="row g-0 min-vh-100 mk-app-row">
        <div className={`${sidebarClass} mk-sidebar-column`}>
          <TerminalSidebar expanded={expanded} onNavigate={() => setMobileOpen(false)} />
        </div>
        <div className={`${mainClass} mk-main-column`}>
          <TerminalHeader onToggle={toggle} />
          <div className="mk-route-content"><AppRoutes /></div>
        </div>
      </div>
      <button className="sidebar-overlay" aria-label="Close navigation" onClick={() => setMobileOpen(false)} />
      <MobileBottomNav />
      </div>
    </>
  );
};

function App() { return <BrowserRouter><AppLayout /></BrowserRouter>; }
export default App;
